import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, BookOpen, TrendingUp, LogOut, Plus, MessageCircle, Crown, KeyRound } from 'lucide-react';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [myCourses, setMyCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const [coursesRes, progressRes, unreadRes] = await Promise.all([
        fetch(`${API}/user/courses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/progress`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/messages/unread/count`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (coursesRes.ok && progressRes.ok) {
        setMyCourses(await coursesRes.json());
        setProgress(await progressRes.json());
      }
      if (unreadRes.ok) {
        const data = await unreadRes.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const completedExercises = progress.filter(p => p.completed).length;
  const totalExercises = myCourses.reduce((acc, course) => acc + (course.exercise_ids?.length || 0), 0);
  const completionRate = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="text-xl sm:text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
            Move<span className="text-[#C6A97C]">More</span>
          </div>
          <div className="flex gap-2 sm:gap-4 items-center">
            <Button 
              data-testid="community-btn" 
              onClick={() => navigate('/community')} 
              className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full text-xs sm:text-sm px-3 sm:px-4"
            >
              <Activity className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Community</span>
            </Button>
            <Button 
              data-testid="messages-btn" 
              onClick={() => navigate('/messages')} 
              variant="outline" 
              className="rounded-full relative text-xs sm:text-sm px-3 sm:px-4"
            >
              <MessageCircle className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Messages</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <span className="text-[#0B0B0B] hidden sm:inline">{user?.name}</span>
            {user?.subscription_status === 'active' && (
              <span className="bg-[#D9C7A3] text-[#0B0B0B] px-2 sm:px-3 py-1 rounded-full text-xs font-bold">PRO</span>
            )}
            <Button data-testid="change-password-btn" onClick={() => setShowChangePassword(true)} variant="outline" className="rounded-full text-xs sm:text-sm px-2 sm:px-4" title="Change password">
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="outline" className="rounded-full text-xs sm:text-sm px-2 sm:px-4">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12" data-testid="client-dashboard">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-[#0B0B0B] mb-2" style={{ fontFamily: 'Clash Display' }}>Welcome back, {user?.name}!</h1>
          <p className="text-gray-600 text-sm sm:text-base">Track your recovery journey and stay consistent</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="p-6 bg-white rounded-2xl">
            <Activity className="w-10 h-10 text-[#0B0B0B] mb-4" />
            <div className="text-3xl font-bold text-[#0B0B0B] mb-1">{completedExercises}</div>
            <div className="text-gray-600">Exercises Completed</div>
          </Card>
          <Card className="p-6 bg-white rounded-2xl">
            <BookOpen className="w-10 h-10 text-[#0B0B0B] mb-4" />
            <div className="text-3xl font-bold text-[#0B0B0B] mb-1">{myCourses.length}</div>
            <div className="text-gray-600">Active Programs</div>
          </Card>
          <Card className="p-6 bg-white rounded-2xl">
            <TrendingUp className="w-10 h-10 text-[#0B0B0B] mb-4" />
            <div className="text-3xl font-bold text-[#0B0B0B] mb-1">{completionRate}%</div>
            <div className="text-gray-600">Overall Progress</div>
          </Card>
        </div>

        {user?.subscription_status !== 'active' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Card className="p-6 bg-gradient-to-r from-[#0B0B0B] to-[#161616] rounded-2xl" data-testid="subscription-cta-card">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D9C7A3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6 text-[#0B0B0B]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Clash Display' }}>
                      Unlock All Courses
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Subscribe for unlimited access to every course and future content
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="subscribe-cta-btn"
                  onClick={() => navigate('/subscription')}
                  className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-8 font-bold whitespace-nowrap"
                >
                  View Plans
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>My Programs</h2>
            <Button data-testid="browse-more-courses-btn" onClick={() => navigate('/courses')} className="bg-[#0B0B0B] text-white hover:bg-[#161616] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Browse More
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading your programs...</div>
          ) : myCourses.length === 0 ? (
            <Card className="p-12 bg-white rounded-2xl text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0B0B0B] mb-2">No programs yet</h3>
              <p className="text-gray-600 mb-6">Start your recovery journey by enrolling in a program</p>
              <Button data-testid="get-started-programs-btn" onClick={() => navigate('/courses')} className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-8">
                Browse Programs
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myCourses.map((course, index) => {
                const courseProgress = progress.filter(p => course.exercise_ids?.includes(p.exercise_id));
                const completed = courseProgress.filter(p => p.completed).length;
                const total = course.exercise_ids?.length || 0;
                const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

                return (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => navigate(`/courses/${course.id}`)}
                    className="cursor-pointer"
                    data-testid={`course-card-${index}`}
                  >
                    <Card className="p-6 bg-white rounded-2xl hover:shadow-lg transition-shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-xs uppercase tracking-wide text-[#0B0B0B]/60 mb-1">{course.category}</div>
                          <h3 className="text-xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>{course.title}</h3>
                        </div>
                        <div className="bg-[#D9C7A3] text-[#0B0B0B] px-3 py-1 rounded-full text-xs font-bold">{course.difficulty}</div>
                      </div>
                      <p className="text-gray-600 mb-4 text-sm line-clamp-2">{course.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-bold text-[#0B0B0B]">{completed}/{total} exercises</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {progress.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-[#0B0B0B] mb-6" style={{ fontFamily: 'Clash Display' }}>Recent Activity</h2>
            <Card className="bg-white rounded-2xl p-6">
              <div className="space-y-4">
                {progress.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.completed ? 'bg-[#D9C7A3] text-[#0B0B0B]' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <Activity className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-medium text-[#0B0B0B]">Exercise Session</div>
                        <div className="text-sm text-gray-600">{new Date(item.completed_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    {item.pain_level !== null && (
                      <div className="text-sm">
                        <span className="text-gray-600">Pain level:</span> <span className="font-bold text-[#0B0B0B]">{item.pain_level}/10</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
